import { BRAND_COLOR_MAX_LENGTH } from '@t4s/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type FormEvent, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { ApiFailure, codeOf, fieldErrorsOf } from '../lib/api-error.ts'
import { messageFor } from '../lib/messages.ts'
import { NotFoundScreen } from '../screens/not-found.tsx'
import { useDocumentTitle } from '../shell/document-title.ts'
import { Notice } from '../shell/notice.tsx'
import { fetchTenant, tenantQueryKey } from '../tenants/gateway.ts'
import { TenantHeader } from '../tenants/tenant-header.tsx'
import { brandColourState, cssColour, pickerColour } from './brand-colour.ts'
import { ColourPreview } from './colour-preview.tsx'
import { brandingQueryKey, fetchTenantBranding, saveTenantBranding } from './gateway.ts'
import { LogoPreview, useSettledValue } from './logo-preview.tsx'

/**
 * Branding — docs/ux/PLATFORM-ADMIN.md §13.
 *
 * **P04 built the read half** — the corrected colour, the two-scheme preview, the
 * propagation limit, and the config-file download — because `PATCH
 * /platform/tenants/{tenantId}/branding` did not exist yet. **P04a-api built that
 * endpoint. This task (P04b) builds the write half against it**: two fields only
 * (`brandColor`, `logoUrl`), matching `UpdateBrandingRequest` exactly — no `corrected`, no
 * `fallback`, no third field, since the write endpoint has no room for either
 * (§13.2, and `docs/task-specs/P04a-api.md`).
 *
 * **The download and the preview read the same query, and a save writes back into it.**
 * Reusing the query that already loaded the screen for the download itself, rather than
 * issuing a second call on click, is what keeps the preview and the downloaded file in
 * agreement. A save's response is the same regenerated shape, so it replaces that query's
 * cached data directly (`queryClient.setQueryData`) rather than triggering a second read.
 *
 * **P07 (2026-10-14) made the preview follow what is being typed.** The "What staff will see"
 * card (`colour-preview.tsx`) and the logo box (`logo-preview.tsx`) are handed the colour and the
 * logo the screen decides to preview: the field's text once edited, the stored document otherwise,
 * and the stored document again while a save is in flight and after one fails (§13.4) until the next
 * edit. The screen decides; the cards only draw. See `docs/DECISIONS.md`, "The console's branding
 * preview follows the keyboard…".
 *
 * **No `kid`, no mint time.** Those were the signed build identifier's; `buildKey` is a
 * plain value copied verbatim from the tenant row (`docs/DECISIONS.md`, "The platform
 * console's config-file download is not a credential", amended 2026-08-24), so this screen
 * has nothing of that shape left to show. The tenant page's "Build key" panel is where a
 * rotation happens and where the consequence of one is stated.
 */

export type BrandingScreenProps = {
  readonly tenantId: string
}

/**
 * `UpdateBrandingRequest.brandColor`'s own `maxLength` (`apps/api/openapi.json`) — imported
 * from `@t4s/domain` (`chore-brandcolor-max-single-source`) rather than restated as a bare
 * `64` here, so this screen's clamp cannot drift from the route's own boundary or from
 * `packages/white-label`'s stored-config schema, both of which import the identical
 * constant. Used at each of its call sites below — the `onChange` clamp and cut-detector,
 * the over-long-stored-value notice, the cut notice, and the mutation's own defensive clamp
 * (see `fix-console-brandcolor-length-enforcement`'s "M6" and "M2" findings, and
 * `chore-console-brandcolor-truncation-notice`).
 *
 * **The input carries no native `maxLength` attribute.** It did until
 * `chore-console-brandcolor-truncation-notice`, which removed it: a native `maxLength` blocks
 * an over-boundary edit at the DOM level before any `change` event fires at all (confirmed
 * against this project's own `@testing-library/user-event`, and true of a real browser by the
 * HTML specification, not a test-harness quirk — `maxLength` constrains what a user agent
 * *allows to be inserted*, not what fires an event), which is exactly why the typing-past-64
 * case could never be given a notice while the attribute did the clamping: there was no event
 * for a notice to hang off. Enforcement moved entirely into the `onChange` handler below,
 * which already ran on every change event (added by `fix-console-brandcolor-length-enforcement`
 * for its M2 finding) and now also measures how many characters each edit cut.
 *
 * **Amended in PR #178's own review round 1 (approved with a requested fix, not a blocker the
 * approval waived): the `onChange` clamp originally shipped here (`raw.slice(0,
 * BRAND_COLOR_MAX_LENGTH)`) cut from the end of the whole string, not from where the edit
 * happened.** For typing or pasting at the end of the field that is the same thing; for a paste
 * in the middle of an already-long value, it is not — the reviewer's own jsdom probe (a
 * 60-character base, a 20-character paste at position 30, pushing the field to 80 characters)
 * showed the end-anchored clamp kept the entire pasted insertion and silently dropped 16
 * characters of the operator's **untouched tail** instead, landing the caret at the very end
 * (64) rather than where the edit happened. Native `maxLength`, what this task's own fix
 * otherwise replaces, did the opposite: it truncated the *paste itself* to only what fit (4 of
 * the 20 characters), preserved the rest of the tail, and left the caret right after the
 * truncated insertion (34) — a materially less disorienting result, and the one this clamp now
 * reproduces. See `clampBrandColorEdit` below for the mechanism and
 * `docs/DECISIONS.md` for the fuller record.
 */

/**
 * The fields this form's own `validation_failed` can name — `chore-platform-field-error-
 * mapping`. `PATCH /platform/tenants/{tenantId}/branding`'s request body
 * (`UpdateBrandingRequestSchema`) is flat, so both paths are bare field names.
 *
 * **`brandColor`'s own min-length and this screen's own clamp (`clampBrandColorEdit`, the
 * `BRAND_COLOR_MAX_LENGTH` constant above) make its server-side check almost entirely
 * unreachable through this form** — the field cannot be submitted blank (Save disables on
 * `brandColor.trim() === ''`) and cannot be typed, pasted or programmatically set past the
 * boundary (every path is clamped before it reaches state). It is still mapped, for the one
 * path the clamp does not cover: a *stored* value already longer than the boundary,
 * resent unshortened because the operator's edit touched only `logoUrl` this visit — see
 * `storedColorTooLong`'s own notice above, which already covers this, but a server refusal
 * of that exact resend still deserves to land on the field rather than the generic notice.
 * **`logoUrl` is genuinely reachable**: its `type="url"` input accepts `http://` and any
 * other syntactically valid URL, but `UpdateBrandingRequestSchema.logoUrl` requires
 * `https://` — nothing in this form enforces that scheme client-side.
 */
type Field = 'brandColor' | 'logoUrl'

const FIELD_FOR_PATH: Readonly<Record<string, Field>> = {
  brandColor: 'brandColor',
  logoUrl: 'logoUrl',
}

function copyForField(field: Field): string {
  return field === 'brandColor'
    ? `Must be 1–${BRAND_COLOR_MAX_LENGTH} characters.`
    : 'Must start with https:// or be left blank.'
}

/**
 * What an `onChange` on the Brand colour field should do with its own incoming value: how much
 * of it to keep, how many characters were cut, and — when an insertion was trimmed — where the
 * caret belongs afterwards.
 */
type BrandColorEdit = {
  /** Never longer than `BRAND_COLOR_MAX_LENGTH`. What `brandColor` state becomes. */
  readonly value: string
  /** How many characters this edit's own insertion lost, 0 meaning none. Drives the notice. */
  readonly cut: number
  /**
   * Where to put the caret once React has re-rendered with `value`, or `null` to leave the
   * browser's own placement alone — correct already for an in-bounds edit, and for the one
   * branch below that keeps this clamp's pre-this-amendment behaviour on purpose.
   */
  readonly caret: number | null
}

/**
 * Emulates what native `maxLength` did before this task removed it (see
 * `BRAND_COLOR_MAX_LENGTH`'s own docblock above): an edit that pushes the field over the
 * boundary has only its *own inserted text* trimmed to fit, never the untouched text on either
 * side of it, and the caret lands just after whatever of the insertion survived — not
 * unconditionally at the end of the field, which is what this clamp did before PR #178's review
 * round 1 caught it (see the amendment note above).
 *
 * **How the edited region is found.** Nothing here reads the input's `selectionStart`/
 * `selectionEnd` from *before* the edit — React's `onChange` fires after the DOM has already
 * applied it, so a "before" selection would need its own capture (an `onSelect`/`onKeyDown`
 * listener and a ref) for no real gain, since diffing `prev` (this field's value before the
 * edit) against `next` (`event.target.value`, the already-edited DOM value) for their longest
 * common prefix and, independently, longest common suffix reconstructs the same answer:
 * whatever sits between the common prefix and the common suffix is what this one edit inserted.
 * This correctly covers typing at the end, pasting at a point, and pasting over a selection
 * alike, with no special-casing between them.
 *
 * **A pure deletion has no inserted segment to trim.** A `Backspace`, a cut, or a paste that is
 * itself shorter than what it replaced leaves `next` made entirely of characters `prev` already
 * had, so the common prefix and common suffix between them account for the whole of `next` —
 * `insertedLength` comes out to 0 or less. If `next` is still over the boundary in that case,
 * there is nothing to preserve a *tail of an insertion* for, so this function keeps the
 * end-anchored `.slice(0, BRAND_COLOR_MAX_LENGTH)` it otherwise replaces, unchanged from before
 * this amendment. **Today, the only path that reaches this branch is the pre-filled
 * over-long-stored-value state's collapsing first edit** (`fix-console-brandcolor-length-
 * enforcement`'s "M6") — every other path into this field is already bounded to
 * `BRAND_COLOR_MAX_LENGTH` before a caller could ever hand this function a `next` this large
 * without an insertion, so this is a real, reachable branch, not a defensive one to satisfy the
 * type checker. `docs/ux/PLATFORM-ADMIN.md` §13.4 states why this one case keeps the old,
 * end-anchored behaviour on purpose rather than emulating a tail it has none of.
 */
function clampBrandColorEdit(prev: string, next: string): BrandColorEdit {
  if (next.length <= BRAND_COLOR_MAX_LENGTH) {
    return { value: next, cut: 0, caret: null }
  }

  const maxPrefix = Math.min(prev.length, next.length)
  let prefixLen = 0
  while (prefixLen < maxPrefix && prev[prefixLen] === next[prefixLen]) {
    prefixLen++
  }
  const maxSuffix = Math.min(prev.length, next.length) - prefixLen
  let suffixLen = 0
  while (
    suffixLen < maxSuffix &&
    prev[prev.length - 1 - suffixLen] === next[next.length - 1 - suffixLen]
  ) {
    suffixLen++
  }
  const insertedLength = next.length - prefixLen - suffixLen

  if (insertedLength <= 0) {
    // A pure deletion that is still over the boundary — see this function's own docblock.
    return {
      value: next.slice(0, BRAND_COLOR_MAX_LENGTH),
      cut: next.length - BRAND_COLOR_MAX_LENGTH,
      caret: null,
    }
  }

  const insertionBudget = Math.max(0, BRAND_COLOR_MAX_LENGTH - prefixLen - suffixLen)
  const keptInsertionLength = Math.min(insertedLength, insertionBudget)
  const value =
    next.slice(0, prefixLen) +
    next.slice(prefixLen, prefixLen + keptInsertionLength) +
    next.slice(next.length - suffixLen)

  return {
    value,
    cut: insertedLength - keptInsertionLength,
    caret: prefixLen + keptInsertionLength,
  }
}

/**
 * **One form per tenant id** — `fix-P04b-branding-form-keeps-other-tenant-values` (QA D1,
 * 2026-09-28). The router renders this one component for every tenant's Branding page, so a
 * move straight from one tenant's page to another's (the browser's history menu does it; an
 * in-app link never does, since every in-app path goes through another page first) kept the
 * same instance and changed only `tenantId`. Everything below that lives in `useState` — the
 * two fields, the touched flags, `loaded` — therefore stayed the first tenant's, and Save wrote
 * it onto the second. Keying the form by tenant id discards all of it on a tenant change and
 * starts the form again exactly as on a fresh visit.
 *
 * The key also gives each tenant its own save mutation and its own in-flight guard. A save
 * started for the first tenant still completes against the first tenant — its request was
 * already built — and its `onSuccess` writes into the first tenant's cache entry, because that
 * instance's options stop updating once it unmounts. It cannot show "Saved.", an error, or a
 * pending Save on the second tenant's page. See `docs/DECISIONS.md`, "The Branding form is keyed
 * by tenant id, and sends only the fields the operator edited".
 */
export function BrandingScreen({ tenantId }: BrandingScreenProps) {
  return <BrandingForm key={tenantId} tenantId={tenantId} />
}

function BrandingForm({ tenantId }: BrandingScreenProps) {
  const queryClient = useQueryClient()
  const colourId = useId()
  const colourErrorId = useId()
  const colourHelpId = useId()
  const logoId = useId()
  const logoErrorId = useId()
  const logoHelpId = useId()

  const tenant = useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: () => fetchTenant(tenantId),
  })
  const branding = useQuery({
    queryKey: brandingQueryKey(tenantId),
    queryFn: () => fetchTenantBranding(tenantId),
  })

  useDocumentTitle('Branding', tenant.data?.name, 'Tenants')

  const [brandColor, setBrandColor] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [loaded, setLoaded] = useState(false)
  // "Saved." stays until the operator's first edit after it appeared, a new Save or a failed one
  // (§13.4, `chore-console-spec-gaps-2026-10-01`, G1): each of the three edit handlers below
  // clears it. An edit made while a save is still in flight changes nothing here — "Saved." is
  // not showing yet — and the typed text is kept either way.
  const [saved, setSaved] = useState(false)
  // A save failed, so the preview shows the last saved value rather than something that was not
  // saved (§13.4, "Error") until the next edit — the typed text stays in the fields throughout.
  const [revertedAfterError, setRevertedAfterError] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Field, string>>>({})
  // Whether the operator has edited the colour field this visit — distinct from whether its
  // current value differs from what was loaded. Only a touched colour is ever sent; see the
  // mutation below and docs/ux/PLATFORM-ADMIN.md §13.4 "A stored colour longer than the
  // maximum". This is `fix-console-brandcolor-length-enforcement`'s fix for its "M6" finding:
  // the pre-fill below is a programmatic `setBrandColor`, which — unlike an edit that goes
  // through the input element — never runs the field's own `onChange` handler at all, so a
  // tenant whose *stored* colour already exceeds `BRAND_COLOR_MAX_LENGTH` must not have that
  // value resent (and rejected) merely because Save was pressed for an unrelated `logoUrl` edit.
  const [colourTouched, setColourTouched] = useState(false)
  // The same rule for the logo, since `fix-P04b-branding-form-keeps-other-tenant-values`. The
  // logo used to be sent on every Save, touched or not — which is why QA's D1 needed no edit at
  // all to write one tenant's logo onto another. `UpdateBrandingRequest.logoUrl` is optional and
  // "omit to leave unchanged", exactly like `brandColor`, so an untouched logo is now left out.
  const [logoTouched, setLogoTouched] = useState(false)
  // Set synchronously by `submit` before the save starts, and cleared when it settles — QA L1.
  // `save.isPending` only becomes `true` on the render after `mutate()`, so two submits in the
  // same JavaScript tick (two `requestSubmit()` calls, three `click()`s) both saw `false` and
  // both wrote. A ref is read and written without waiting for a render.
  const savingRef = useRef(false)
  // The field values each save sent, captured when it starts — review B1 on #258. The fields stay
  // editable while a save is in flight (§13.4 makes only the button inert), so by the time the
  // response lands the operator may have typed more. `onSuccess` clears a field's touched flag only
  // when the field still holds exactly what was sent; otherwise the field stays touched, keeps its
  // text against the response, and the next Save sends it. `undefined` means the field was not sent.
  const sentRef = useRef<{ brandColor?: string; logoUrl?: string }>({})
  // How many characters the most recent edit to Brand colour cut, 0 meaning none.
  // `chore-console-brandcolor-truncation-notice`: measured on every `onChange` event — typed,
  // pasted, or a programmatic write that dispatches one — from the event's own incoming value,
  // before it is clamped. Describes only the most recent edit; an edit that does not itself cut
  // anything resets it to 0, it is never a sticky per-visit flag. See the notice this drives,
  // below, and docs/ux/PLATFORM-ADMIN.md §13.4 "Brand colour length".
  const [colourCharsCut, setColourCharsCut] = useState(0)
  // The colour input's own DOM node, so the caret can be restored after a render that
  // `clampBrandColorEdit` asked for one on — a ref because it is an imperative, one-shot
  // action taken *after* React commits the clamped value, not something `render` itself reads.
  const colourInputRef = useRef<HTMLInputElement | null>(null)
  // Set by `onChange` to the caret position `clampBrandColorEdit` computed, or `null` when no
  // caret intervention is needed this edit — read and cleared by the layout effect below, once,
  // on the next commit. A ref rather than state: writing it must never itself schedule a render.
  const pendingCaretRef = useRef<number | null>(null)

  // Runs after every commit, including ones this component did not cause (harmless — it is a
  // no-op whenever `pendingCaretRef.current` is `null`, which is every commit but the one right
  // after `onChange` scheduled a caret restoration below). `setSelectionRange` here, not in
  // `onChange` itself, because at the time `onChange` runs the DOM still holds the *pre-clamp*
  // value — React has not yet re-rendered the input down to the clamped one — so setting the
  // caret before that commit would be positioning it against a value about to be replaced.
  useLayoutEffect(() => {
    const caret = pendingCaretRef.current
    if (caret !== null && colourInputRef.current !== null) {
      colourInputRef.current.setSelectionRange(caret, caret)
    }
    pendingCaretRef.current = null
  })

  // The form is not drawn at all until the document has landed (a "Reading…" panel stands in for
  // it), and on the commit it first appears in its fields are still disabled and empty; the effect
  // below fills and enables them on the next one. So there is never an empty, enabled form that
  // fills in underneath somebody who has started typing (§12.1's rule, applied here as it is on
  // Settings).
  //
  // **Each untouched field follows every document that lands, not only the first** —
  // `fix-P04b-branding-stale-after-external-change` (QA D3, 2026-09-28). Reopening this page
  // mounts a fresh form that is first handed the *cached* document, older than the thirty-second
  // `staleTime`, while a refetch runs in the background. This effect used to fill once (`loaded`)
  // and ignore the refetch, so after the stored branding changed elsewhere the fields kept the old
  // values while the notices, preview and download followed the new ones. Now a newer document —
  // the refetch, or a save's own response written into the cache — replaces every field the
  // operator has not edited on this visit. An edited field keeps what was typed: a document
  // landing never overwrites typing. Save still sends only edited fields, so a field that follows
  // the data changes what the operator sees, never what is written. See `docs/DECISIONS.md`,
  // "The Branding form follows the stored document until a field is edited".
  useEffect(() => {
    if (branding.data === undefined) {
      return
    }
    if (!colourTouched) {
      setBrandColor(branding.data.colors.brand)
    }
    if (!logoTouched) {
      setLogoUrl(branding.data.logoUrl ?? '')
    }
    setLoaded(true)
  }, [branding.data, colourTouched, logoTouched])

  const storedColorTooLong = (branding.data?.colors.brand.length ?? 0) > BRAND_COLOR_MAX_LENGTH

  const save = useMutation({
    // Review S1 on #258: a background read of this document started before the save (the refetch
    // on reopening the page) could answer after the save's response and overwrite the cache with
    // the older document, reverting untouched fields and the preview while "Saved." showed. The
    // save's response is the newer document, so any read already in flight is cancelled first.
    //
    // **It reports whether it cancelled anything** (`fix-P04b-branding-save-failure-double-notice`):
    // `onError` re-reads the document only when a read was actually in flight, because only then
    // did the save take away an answer nothing else will bring back. A save that cancelled nothing
    // and fails has nothing to re-read, and re-reading during an outage just adds a second failure
    // notice and Try again beside the save's own.
    onMutate: async (): Promise<boolean> => {
      const queryKey = brandingQueryKey(tenantId)
      const cancelledARead = queryClient.isFetching({ queryKey }) > 0
      await queryClient.cancelQueries({ queryKey })
      return cancelledARead
    },
    mutationFn: () => {
      // Each field is sent only when the operator edited it on this page — `logoUrl` too, since
      // `fix-P04b-branding-form-keeps-other-tenant-values`, so a Save with nothing edited sends
      // `{}`. The rest of this comment is about `brandColor`, where the rule started.
      // `brandColor` is sent only when the operator actually edited it. `UpdateBrandingRequest`
      // treats an absent field as "leave unchanged" (`apps/api/openapi.json`), so an untouched
      // field — including one pre-filled with a stored value longer than
      // `BRAND_COLOR_MAX_LENGTH`, which never runs the input's own change handler — is never
      // resent and so never blocks an unrelated `logoUrl` edit on a `400` it did nothing to
      // cause. A touched value is clamped a second time here — the
      // `onChange` handler above already clamps on every keystroke-or-equivalent, so `brandColor`
      // itself should never exceed `BRAND_COLOR_MAX_LENGTH` by the time this runs — as a
      // redundant guard for the one shape of caller neither clamp can see: something that
      // writes to this component's own state directly, bypassing both the input element and
      // its change handler, which no code path in this file does today but which costs nothing
      // to guard against here rather than trust.
      const patch: { brandColor?: string; logoUrl?: string | null } = {}
      if (logoTouched) {
        patch.logoUrl = logoUrl.trim() === '' ? null : logoUrl.trim()
      }
      if (colourTouched) {
        patch.brandColor = brandColor.trim().slice(0, BRAND_COLOR_MAX_LENGTH)
      }
      return saveTenantBranding(tenantId, patch)
    },
    onSuccess: (updated) => {
      setSaved(true)
      setRevertedAfterError(false)
      // Review B1: text typed after Save was pressed is not what this response confirms. Only a
      // field still holding what was sent goes back to following the stored document.
      // `brandColor`/`logoUrl`/touched here are the *latest* render's, not Save-time's: TanStack
      // Query v5's `MutationObserver.setOptions` pushes each render's options into the pending
      // mutation. Moving these callbacks into `mutate(…, { onSuccess })` would break that.
      const sent = sentRef.current
      setColourTouched(colourTouched && brandColor !== sent.brandColor)
      setLogoTouched(logoTouched && logoUrl !== sent.logoUrl)
      setFieldErrors({})
      queryClient.setQueryData(brandingQueryKey(tenantId), updated)
    },
    onError: (error, _variables, cancelledARead) => {
      setSaved(false)
      setRevertedAfterError(true)
      // Round 2 S1 on #258: `onMutate` cancelled a read in flight, and on the reopen path that
      // read carries the *newer* stored document. A failed save writes nothing into the cache, so
      // read the document again rather than leave the untouched fields on the older one — but only
      // when a read really was cancelled (`cancelledARead`, from `onMutate`).
      if (cancelledARead === true) {
        void queryClient.invalidateQueries({ queryKey: brandingQueryKey(tenantId) })
      }
      // Nothing typed is lost: `brandColor`/`logoUrl` state above is untouched by a
      // failure. `save.error` carries the code the notice below reads.

      // `chore-platform-field-error-mapping`: a `validation_failed` naming a field this
      // form recognises marks that field and shows no generic notice, matching this
      // task's other three forms.
      const failure = error instanceof ApiFailure ? error : undefined
      if (failure?.code === 'validation_failed') {
        const mapped = fieldErrorsOf<Field>(failure, FIELD_FOR_PATH, copyForField)
        if (Object.keys(mapped).length > 0) {
          setFieldErrors(mapped)
          return
        }
      }
      setFieldErrors({})
    },
    onSettled: () => {
      savingRef.current = false
    },
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    // §13.4: "Saving | The button is inert." The `disabled` attribute below is that signal,
    // but it is exactly that — a rendered attribute, not the guard itself. Anything that
    // reaches this handler a second time while the first write is still in flight (a second
    // `submit` event fired directly at the form, not through the button) must still be a
    // no-op, so the check is repeated here rather than trusted to the DOM alone.
    // `savingRef` is the guard that holds within a single JavaScript tick; `save.isPending` is
    // kept alongside it as the rendered state's own view of the same thing.
    if (savingRef.current || save.isPending) {
      return
    }
    savingRef.current = true
    sentRef.current = {
      ...(colourTouched ? { brandColor } : {}),
      ...(logoTouched ? { logoUrl } : {}),
    }
    setFieldErrors({})
    setSaved(false)
    setRevertedAfterError(false)
    save.mutate()
  }

  const config = branding.data
  const retired = tenant.data?.state === 'retired'

  // What the preview shows (§13.3, §13.4). It follows the value being typed — except while a save
  // is in flight ("the preview keeps the last saved value until the save lands") and after one has
  // failed ("reverts to the last known-good value rather than showing something that was not
  // saved"), when it shows the stored document while the fields keep what was typed. The next edit
  // makes it follow the field again. A blank colour has nothing to preview, so it shows the stored
  // one (Save is inert on a blank colour anyway).
  const heldAtSaved = save.isPending || revertedAfterError
  const typedColour = brandColor.trim()
  const colourFollowsField = !heldAtSaved && colourTouched && typedColour !== ''
  const previewColour =
    config === undefined ? '' : colourFollowsField ? typedColour : config.colors.brand
  const logoFollowsField = !heldAtSaved && logoTouched
  // A logo being typed waits until it stops changing; everything else is immediate.
  const previewLogo = useSettledValue(
    config === undefined ? '' : logoFollowsField ? logoUrl : (config.logoUrl ?? ''),
    !logoFollowsField,
  )

  if (tenant.isError) {
    const code = codeOf(tenant.error)
    if (code === 'not_found' || code === 'validation_failed') {
      // The unmatched-address route's page, not a copy of it (`NotFoundScreen`).
      return <NotFoundScreen />
    }
  }

  const colourChanged = config !== undefined && colourTouched && typedColour !== config.colors.brand
  const logoChanged =
    config !== undefined && logoTouched && logoUrl.trim() !== (config.logoUrl ?? '')
  const footerLine = retired
    ? 'A retired tenant is not configured. Its branding is read-only.'
    : save.isPending
      ? 'Saving…'
      : revertedAfterError
        ? 'Not saved — the preview shows the last saved value; what you typed is still in the fields.'
        : brandColor.trim() === ''
          ? // Save is inert on a blank colour (below), so "Unsaved" would promise a save that the
            // button cannot make, and the colour preview is showing the stored colour, not the
            // (empty) field (P07 QA, observation 2).
            'Cannot be saved while the brand colour is blank — the colour preview shows the saved colour'
          : colourChanged || logoChanged
            ? 'Unsaved — the preview shows what you typed'
            : 'No unsaved changes'

  const previewSwatchReadable =
    config !== undefined && brandColourState(previewColour).state !== 'fallback'

  // The label and the filename are one string (§13.5). The slug is what names the file; until the
  // tenant read has answered there is no slug to name it with, so the control waits — and stays
  // disabled if that read failed — rather than ever naming a tenant id.
  const slugKnown = tenant.data !== undefined
  const fileName = `${tenant.data?.slug ?? ''}-branding.json`

  const describedBy = (helpId: string, errorId: string, hasError: boolean) =>
    hasError ? `${helpId} ${errorId}` : helpId

  return (
    <main className="page">
      <TenantHeader tenantId={tenantId} />
      <h2 className="visually-hidden">Branding</h2>

      {/*
        §1 rule 5, and §13.1's amendment by name: said permanently and above the controls, not only
        after a save succeeds, and not in a tooltip.
      */}
      <Notice tone="info" tag="When it lands">
        <strong>Signed-in staff see a saved change immediately.</strong> Signed-out web screens
        (sign-in, splash) pick it up within a day — the config file is cached for 24 hours. Mobile
        changes only with a new build.
      </Notice>

      {tenant.isError &&
      codeOf(tenant.error) !== 'not_found' &&
      codeOf(tenant.error) !== 'validation_failed' ? (
        <Notice tone="failure">{messageFor('branding', codeOf(tenant.error))}</Notice>
      ) : null}

      {retired ? (
        <Notice tone="warning">
          This tenant is retired. Its real branding stays readable and downloadable from this screen
          even though the public config route now serves this tenant’s slug the neutral, unbranded
          default — but it is read-only: the form below is disabled.
        </Notice>
      ) : null}

      {branding.isPending ? (
        <div className="panel">
          <p className="muted">Reading this tenant’s branding document…</p>
        </div>
      ) : null}

      {/*
        A failed read of a document already on screen does not add its own notice beside a failed
        save's (`fix-P04b-branding-save-failure-double-notice`): the save's sentence is the one the
        operator is looking for, and the form with its typed values stays where it is. A read that
        produced nothing, or a refresh failing with no save failure to explain, still says so.
      */}
      {branding.isError && !(config !== undefined && save.isError) ? (
        <>
          <Notice tone="failure">{messageFor('branding', codeOf(branding.error))}</Notice>
          <button
            type="button"
            className="button"
            onClick={() => {
              void branding.refetch()
            }}
          >
            Try again
          </button>
        </>
      ) : null}

      {config === undefined ? null : (
        <div className="columns">
          <form
            className="card columns-branding-form"
            aria-labelledby="colour"
            onSubmit={submit}
            noValidate
          >
            <div className="card-head">
              <div>
                <h2 id="colour">Colour and logo</h2>
                <p className="muted">
                  The company name comes from{' '}
                  <Link to="/tenants/$tenantId/settings" params={{ tenantId }}>
                    Settings
                  </Link>
                  . App name, bundle id, icon and splash need a rebuild and an app-store submission.
                </p>
              </div>
            </div>

            <div className="card-body form">
              {storedColorTooLong && !colourTouched ? (
                <Notice tone="warning">
                  The colour stored for this tenant is {config.colors.brand.length} characters,
                  longer than the {BRAND_COLOR_MAX_LENGTH} this form now allows for a new value. It
                  is shown unshortened and is left exactly as stored unless you edit this field
                  yourself — saving any other change on this screen does not require shortening it
                  first.
                </Notice>
              ) : null}

              {/*
                chore-console-brandcolor-truncation-notice: names the most recent edit's cut, not a
                sticky per-visit flag — it clears the moment an edit does not itself cut anything.
                Mutually exclusive with the notice above: this one only ever fires from an
                `onChange` event, which is what sets `colourTouched`, so the two are never shown for
                the same edit.
              */}
              {colourCharsCut > 0 ? (
                <Notice tone="warning">
                  Brand colour holds at most {BRAND_COLOR_MAX_LENGTH} characters. {colourCharsCut}{' '}
                  character{colourCharsCut === 1 ? '' : 's'} just went over that and{' '}
                  {colourCharsCut === 1 ? 'was' : 'were'} cut — what’s shown here, not what you
                  typed, pasted or deleted down to, is what will be saved.
                </Notice>
              ) : null}

              {saved ? <Notice tone="success">Saved.</Notice> : null}
              {/* `chore-platform-field-error-mapping`: a `validation_failed` mapped onto
                  Brand colour and/or Logo URL below shows no generic notice here — this
                  mirrors `create-tenant.tsx`'s and the other two forms' identical rule. */}
              {save.isError && Object.keys(fieldErrors).length === 0 ? (
                <Notice tone="failure">{messageFor('branding', codeOf(save.error))}</Notice>
              ) : null}

              <div className="field">
                <label htmlFor={colourId}>Brand colour</label>
                <div className="colour-row">
                  {/*
                    The native colour picker, wearing the swatch as its face (§13.1): the swatch is
                    still the truthful preview — dashed when the value cannot be read — and the
                    real `<input type="color">` lies invisibly over all of it, so a press anywhere
                    on the control opens the browser's own picker. It is a labelled input in its
                    own right, with the same disabled rule as the text field.
                  */}
                  <label className="swatch-picker">
                    <span
                      className={previewSwatchReadable ? 'swatch' : 'swatch swatch-empty'}
                      aria-hidden="true"
                      // The colour being previewed, drawn only when `buildColors` read it as one.
                      style={
                        previewSwatchReadable
                          ? { backgroundColor: cssColour(previewColour) }
                          : undefined
                      }
                    />
                    <span aria-hidden="true">Pick</span>
                    <input
                      type="color"
                      aria-label="Pick the brand colour"
                      value={pickerColour(brandColor, config.colors.brand)}
                      disabled={!loaded || retired}
                      onChange={(event) => {
                        // A pick is an edit exactly like typing: same state, same touched flag, so
                        // the preview, the footer and the Save body all treat it as typed text. The
                        // browser always hands back `#rrggbb`, 7 characters, so there is nothing
                        // to clamp.
                        setSaved(false)
                        setColourCharsCut(0)
                        setBrandColor(event.target.value)
                        setColourTouched(true)
                        setRevertedAfterError(false)
                      }}
                    />
                  </label>
                  <input
                    id={colourId}
                    ref={colourInputRef}
                    className="mono"
                    value={brandColor}
                    required
                    disabled={!loaded || retired}
                    aria-invalid={fieldErrors.brandColor !== undefined}
                    aria-describedby={describedBy(
                      colourHelpId,
                      colourErrorId,
                      fieldErrors.brandColor !== undefined,
                    )}
                    onChange={(event) => {
                      // No native `maxLength` any more (chore-console-brandcolor-truncation-notice)
                      // — removed because it blocks an over-boundary edit at the DOM level before
                      // any `change` event fires, which left the typing-past-64 case with no event
                      // a notice could ever hang off. Enforcement lives entirely here now, and runs
                      // on every change event, native or programmatic alike (typing, pasting,
                      // `fireEvent.change`, and every `.fill()`-shaped browser-automation write —
                      // this repository's own `e2e/web/fixtures/auth.ts` included).
                      // `clampBrandColorEdit` (above) does the actual work: it still bounds the
                      // field to `BRAND_COLOR_MAX_LENGTH` so it never shows more than this form
                      // will ever send (unchanged from `fix-console-brandcolor-length-
                      // enforcement`'s own fix), and it trims only *this edit's own insertion*
                      // rather than the whole string from the end (PR #178 review round 1) —
                      // untouched text on either side of a mid-string paste survives, and the
                      // caret (restored by the layout effect above, once React has committed the
                      // clamped value) lands just after whatever of the insertion was kept.
                      const edit = clampBrandColorEdit(brandColor, event.target.value)
                      setSaved(false)
                      setColourCharsCut(edit.cut)
                      setBrandColor(edit.value)
                      setColourTouched(true)
                      setRevertedAfterError(false)
                      pendingCaretRef.current = edit.caret
                    }}
                  />
                </div>
                {fieldErrors.brandColor === undefined ? null : (
                  <p className="field-error" id={colourErrorId}>
                    {fieldErrors.brandColor}
                  </p>
                )}
                <p className="field-help" id={colourHelpId}>
                  A hex colour such as #0b5fff, up to {BRAND_COLOR_MAX_LENGTH} characters. Never
                  refused: an unreadable value falls back to the platform default and a low-contrast
                  one is corrected. If an edit goes over {BRAND_COLOR_MAX_LENGTH}, a notice says how
                  much was cut.
                </p>
              </div>

              <div className="field">
                <label htmlFor={logoId}>Logo URL</label>
                <input
                  id={logoId}
                  className="mono"
                  type="url"
                  value={logoUrl}
                  placeholder="https://…"
                  maxLength={2048}
                  disabled={!loaded || retired}
                  aria-invalid={fieldErrors.logoUrl !== undefined}
                  aria-describedby={describedBy(
                    logoHelpId,
                    logoErrorId,
                    fieldErrors.logoUrl !== undefined,
                  )}
                  onChange={(event) => {
                    setSaved(false)
                    setLogoUrl(event.target.value)
                    setLogoTouched(true)
                    setRevertedAfterError(false)
                  }}
                />
                {fieldErrors.logoUrl === undefined ? null : (
                  <p className="field-error" id={logoErrorId}>
                    {fieldErrors.logoUrl}
                  </p>
                )}
                <p className="field-help" id={logoHelpId}>
                  https:// only. Leave blank to use the default logo. A logo is uploaded to a URL
                  elsewhere first; this field takes that address, not a device path or a pasted
                  image.
                </p>
                <LogoPreview value={previewLogo} />
              </div>
            </div>

            <div className="card-foot">
              <span className="unsaved" role="status">
                {footerLine}
              </span>
              <button
                type="submit"
                className="button button-primary"
                disabled={!loaded || retired || save.isPending || brandColor.trim() === ''}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>

          <ColourPreview
            brand={previewColour}
            typed={colourFollowsField && typedColour !== config.colors.brand}
          />
        </div>
      )}

      <section className="card" aria-labelledby="download">
        <div className="card-head">
          <div>
            <h2 id="download">Config file</h2>
            <p className="muted">
              The same file the tenant’s shared web app fetches. Public — no masking, no expiry;
              download as often as useful.
            </p>
          </div>
          {/* Disabled, not absent, while there is nothing to download yet (§13.5). */}
          <button
            type="button"
            className="button"
            disabled={config === undefined || !slugKnown}
            onClick={() => {
              if (config !== undefined) {
                downloadBrandingConfig(config, fileName)
              }
            }}
          >
            {slugKnown ? (
              <>
                Download <code>{fileName}</code>
              </>
            ) : (
              'Download the config file'
            )}
          </button>
        </div>
      </section>
    </main>
  )
}

/**
 * Saves the already-fetched config document as a file — no second network call, so the
 * document downloaded is exactly the one shown on screen (§13.5).
 */
function downloadBrandingConfig(config: unknown, fileName: string): void {
  const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  } finally {
    URL.revokeObjectURL(url)
  }
}
